import { useState } from 'react'
import { useNavigate } from 'react-router'
import { QRCodeSVG } from 'qrcode.react'
import { api } from '../lib/api'
import CopyLink from './CopyLink'
import type { DraftResponse, DraftState } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
}

export default function StartPanel({ draft, commissionerToken }: Props) {
  const navigate = useNavigate()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const joinUrl = `${window.location.origin}/join/${draft.join_code}`
  const boardUrl = `${window.location.origin}/board/${draft.join_code}`
  const claimedCount = draft.teams.filter((team) => team.claimed).length
  const canStart = draft.status === 'setup' && draft.teams.length >= 2

  async function handleStart() {
    setStarting(true)
    setError(null)
    try {
      await api<DraftResponse>(`/drafts/${draft.join_code}/start`, {
        method: 'POST',
        commissionerToken,
      })
      // Straight to the control panel: from here on the commissioner is
      // running the draft, not configuring it.
      navigate(`/control#${encodeURIComponent(commissionerToken)}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start draft')
      setStarting(false)
    }
  }

  return (
    <section className="panel p-6">
      <h2 className="eyebrow">Draft night</h2>
      <div className="mt-4 flex flex-col gap-6 sm:flex-row">
        {/* Deliberately un-themed, like JoinQr: a QR needs maximum luminance
            contrast to scan, so this stays white whatever the surface is. */}
        <div className="self-start rounded-lg bg-white p-3">
          <QRCodeSVG value={joinUrl} size={128} />
        </div>
        <div className="min-w-0 flex-1 space-y-4 text-sm">
          <div>
            <p className="text-ink-mid">
              Owners join with code{' '}
              <span className="font-mono text-base font-bold tracking-widest text-signal">
                {draft.join_code}
              </span>{' '}
              or by scanning the QR code.
            </p>
            <CopyLink url={joinUrl} />
          </div>
          <CopyLink label="Put the board on the TV:" url={boardUrl} />
          <div className="border-t border-line pt-4">
            <p className="text-ink-mid">
              {claimedCount} of {draft.teams.length} teams joined
            </p>
            {error && <p className="mt-1 text-sm text-crit">{error}</p>}
            <button
              type="button"
              disabled={!canStart || starting}
              onClick={() => void handleStart()}
              className={
                canStart && !starting
                  ? 'mt-2 rounded bg-signal px-4 py-2 font-semibold text-ink hover:bg-signal-dim'
                  : 'mt-2 cursor-not-allowed rounded bg-surface-3 px-4 py-2 font-semibold text-ink-lo'
              }
            >
              {starting ? 'Starting…' : 'Start draft'}
            </button>
            {!canStart && draft.status === 'setup' && draft.teams.length < 2 && (
              <p className="mt-1 text-xs text-ink-lo">Add at least 2 teams to start</p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
